/**
 * Organizations API
 *
 * Functions for interacting with organization endpoints.
 */

import { api, getCurrentOrgSlug } from './client';
import type { EventReminder } from './auth';

/** Weekday tokens for the digest send-day settings (match backend zod enum). */
export type DigestWeekday = 'Sun' | 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat';

/** One scheduled send moment for the unanswered-work reminders, org-local. */
export interface PendingNudgeSlot {
  day: DigestWeekday;
  hour: number;
}

/** Non-user recipient of the org-wide weekly digest or the admins digest. */
export interface DigestExternalRecipient {
  name: string;
  email: string;
}

// =============================================================================
// Types
// =============================================================================

export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  logo?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  isActive: boolean;
  paymentTrackingEnabled: boolean;
  /** A crew member's own decline also marks them Not available for the event. Off by default. */
  declineSetsUnavailable: boolean;
  /** Auto-applied rate for NEW crew members, cents; null = no default. */
  defaultCrewHourlyRate?: number | null;
  defaultCrewDailyRate?: number | null;
  timezone: string;
  // Org-wide notification settings (Admin > Settings > Notifications).
  notifyOnEventPublished: boolean;
  notifyOnEventChanges: boolean;
  notifyAdminsOnDecline: boolean;
  smsNotificationsEnabled: boolean;
  /** Scheduled reminders for unanswered work (availability + confirmations). */
  pendingNudgeEnabled: boolean;
  /** Up to 2 org-local send moments; null/[] = none configured. */
  pendingNudgeSlots?: PendingNudgeSlot[] | null;
  /** Admins digest: daily owners/admins email (action-needed list + event
      briefs for the next 7 days). On by default. */
  adminDigestEnabled: boolean;
  /** Up to 2 org-local send hours (0-23), e.g. [8, 18]. */
  adminDigestHours: number[];
  /** Non-user recipients of the admins digest (briefs only); null = none. */
  adminDigestExternalRecipients?: DigestExternalRecipient[] | null;
  weeklyDigestEnabled: boolean;
  /** Send moment for the crew assignment digest, org-local. */
  weeklyDigestDay: DigestWeekday;
  weeklyDigestHour: number;
  /** Org-wide weekly digest (everyone in the org + external recipients). */
  orgWeeklyDigestEnabled: boolean;
  orgWeeklyDigestDay: DigestWeekday;
  orgWeeklyDigestHour: number;
  /** Non-user recipients of the org-wide digest; null = none. */
  digestExternalRecipients?: DigestExternalRecipient[] | null;
  /** Reminders seeded into NEW crew members (email/push only); null = none. */
  defaultCrewReminders?: EventReminder[] | null;
  /** Computed server-side: the org's effective tier includes outbound SMS. */
  smsTierAllowed?: boolean;
  /** Org switch for ICS calendar subscriptions (Settings > Integrations).
      The only gate — not tier-gated; defaults ON, owners can turn it off. */
  calendarFeedEnabled: boolean;
  /** Archive crew off the roster after this many past events with no
      response. Off by default; archiving never removes access. */
  autoArchiveEnabled: boolean;
  autoArchiveAfterEvents: number;
  createdAt: string;
  updatedAt: string;
  createdById: string;
  memberCount?: number;
  eventCount?: number;
}

export interface UpdateOrganizationInput {
  name?: string;
  description?: string | null;
  logo?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  paymentTrackingEnabled?: boolean;
  declineSetsUnavailable?: boolean;
  defaultCrewHourlyRate?: number | null;
  defaultCrewDailyRate?: number | null;
  timezone?: string;
  notifyOnEventPublished?: boolean;
  notifyOnEventChanges?: boolean;
  notifyAdminsOnDecline?: boolean;
  smsNotificationsEnabled?: boolean;
  pendingNudgeEnabled?: boolean;
  pendingNudgeSlots?: PendingNudgeSlot[] | null;
  adminDigestEnabled?: boolean;
  adminDigestHours?: number[];
  adminDigestExternalRecipients?: DigestExternalRecipient[] | null;
  weeklyDigestEnabled?: boolean;
  weeklyDigestDay?: DigestWeekday;
  weeklyDigestHour?: number;
  orgWeeklyDigestEnabled?: boolean;
  orgWeeklyDigestDay?: DigestWeekday;
  orgWeeklyDigestHour?: number;
  digestExternalRecipients?: DigestExternalRecipient[] | null;
  calendarFeedEnabled?: boolean;
  defaultCrewReminders?: EventReminder[] | null;
  autoArchiveEnabled?: boolean;
  autoArchiveAfterEvents?: number;
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Get current organization details
 */
export async function getOrganization(): Promise<{ organization: Organization }> {
  const orgSlug = getCurrentOrgSlug();
  if (!orgSlug) {
    throw new Error('No organization selected');
  }
  const response = await api.get<{ organization: Organization }>(
    `/organizations/${orgSlug}`
  );
  return response.data;
}

/**
 * Update current organization
 */
export async function updateOrganization(
  data: UpdateOrganizationInput
): Promise<{ organization: Organization; message: string }> {
  const orgSlug = getCurrentOrgSlug();
  if (!orgSlug) {
    throw new Error('No organization selected');
  }
  const response = await api.put<{ organization: Organization; message: string }>(
    `/organizations/${orgSlug}`,
    data
  );
  return response.data;
}

// =============================================================================
// Admins digest: manual send
// =============================================================================

/** 'preview' = today's digest to me only; 'everyone' = all recipients now. */
export type AdminDigestSendMode = 'preview' | 'everyone';

/**
 * Queue a manual admins-digest send. The server only enqueues (the worker
 * builds and sends), so the email lands a minute or so later. Uses the SAVED
 * settings; neither mode touches the scheduled send times.
 */
export async function sendAdminDigest(
  mode: AdminDigestSendMode
): Promise<{ queued: true; mode: AdminDigestSendMode }> {
  const orgSlug = getCurrentOrgSlug();
  if (!orgSlug) {
    throw new Error('No organization selected');
  }
  const response = await api.post<{ queued: true; mode: AdminDigestSendMode }>(
    `/organizations/${orgSlug}/admin-digest/send`,
    { mode }
  );
  return response.data;
}
